import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import AdminLayout from '@/components/admin/AdminLayout'
import Button from '@/components/ui/Button'
import Notification from '@/components/ui/Notification'
import { FormInput, FormSelect } from '@/components/ui/form'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface UserData {
  id: number
  name: string | null
  email: string
  role_id: number | null
  role_name: string | null
  sign_in_count: number
  last_sign_in_at: string | null
  current_sign_in_ip: string | null
}

interface RoleOption { value: string; label: string }

interface Props {
  user: UserData
  all_roles: RoleOption[]
}

// ─── Component ──────────────────────────────────

export default function UsersShow({ user, all_roles }: Props) {
  const [formName, setFormName] = useState(user.name || '')
  const [formEmail, setFormEmail] = useState(user.email)
  const [formRoleId, setFormRoleId] = useState(user.role_id ? String(user.role_id) : '')
  const [formPassword, setFormPassword] = useState('')
  const [formPasswordConfirmation, setFormPasswordConfirmation] = useState('')
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''

  const notify = (msg: string) => { setNotifMessage(msg); setShowNotif(true) }

  const handleSave = async () => {
    if (!formEmail.trim()) { setFormErrors(['Email is required']); return }
    setFormErrors([]); setSaving(true)
    try {
      const body: Record<string, string | number | null> = {
        name: formName.trim(), email: formEmail.trim(),
        role_id: formRoleId ? Number(formRoleId) : null,
      }
      if (formPassword) {
        body.password = formPassword
        body.password_confirmation = formPasswordConfirmation
      }
      const res = await fetch(`/admin/users/${user.id}.json`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ user: body }),
      })
      if (res.ok) {
        notify('success:Saved:User updated successfully')
        setFormPassword('')
        setFormPasswordConfirmation('')
      } else {
        const err = await res.json().catch(() => ({}))
        setFormErrors(err.errors || ['Failed to save'])
      }
    } catch { setFormErrors(['Network error']) }
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this user?')) return
    try {
      const res = await fetch(`/admin/users/${user.id}.json`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken },
      })
      if (res.ok) {
        router.visit('/admin/users')
      }
    } catch { /* silent */ }
  }

  return (
    <>
      <Head title={`Edit ${user.name || 'User'}`} />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />
      <AdminLayout>
      <main>
        {/* Secondary nav - back link */}
        <header className="border-b border-gray-200 dark:border-zinc-700">
          <div className="flex items-start justify-between py-4 px-4 sm:px-6 lg:px-8">
            <div>
              <Link href="/admin/users" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200">
                <ArrowLeftIcon className="h-4 w-4" /> Back to Users
              </Link>
              <h1 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">{user.name || 'User'}</h1>
            </div>
            <div className="flex items-center gap-3">
              <Link href="/admin/users"><Button variant="secondary">Cancel</Button></Link>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</Button>
            </div>
          </div>
        </header>

        {/* Error banner */}
        {formErrors.length > 0 && (
          <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">{formErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          </div>
        )}

        {/* Settings sections */}
        <div className="divide-y divide-gray-200 dark:divide-zinc-700">
          {/* Section 1: Personal Information */}
          <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Personal Information</h2>
              <p className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">Basic user details and role assignment.</p>
            </div>
            <div className="md:col-span-2">
              <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">Name</label>
                  <div className="mt-2">
                    <FormInput value={formName} onChange={e => setFormName((e.target as HTMLInputElement).value)} placeholder="Full name..." />
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">Email</label>
                  <div className="mt-2">
                    <FormInput value={formEmail} onChange={e => setFormEmail((e.target as HTMLInputElement).value)} placeholder="user@example.com" />
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">Role</label>
                  <div className="mt-2">
                    <FormSelect value={formRoleId} onChange={v => setFormRoleId(v)} options={all_roles} placeholder="Select role..." />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Change Password */}
          <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Change Password</h2>
              <p className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">Leave blank to keep the current password.</p>
            </div>
            <div className="md:col-span-2">
              <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
                <div className="col-span-full">
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">New Password</label>
                  <div className="mt-2">
                    <FormInput type="password" value={formPassword} onChange={e => setFormPassword((e.target as HTMLInputElement).value)} placeholder="New password..." />
                  </div>
                </div>
                <div className="col-span-full">
                  <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">Confirm Password</label>
                  <div className="mt-2">
                    <FormInput type="password" value={formPasswordConfirmation} onChange={e => setFormPasswordConfirmation((e.target as HTMLInputElement).value)} placeholder="Confirm password..." />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Account Info (read-only) */}
          <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Account Info</h2>
              <p className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">Read-only sign-in statistics.</p>
            </div>
            <div className="md:col-span-2">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-6 sm:max-w-xl sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <dt className="text-sm/6 font-medium text-gray-900 dark:text-white">Sign-in Count</dt>
                  <dd className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">{user.sign_in_count}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm/6 font-medium text-gray-900 dark:text-white">Last Sign In</dt>
                  <dd className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">{user.last_sign_in_at || 'Never'}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm/6 font-medium text-gray-900 dark:text-white">Current IP</dt>
                  <dd className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">{user.current_sign_in_ip || '-'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Section 4: Delete User */}
          <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Delete User</h2>
              <p className="mt-1 text-sm/6 text-gray-500 dark:text-zinc-400">Permanently remove this user account. This action cannot be undone.</p>
            </div>
            <div className="flex items-start md:col-span-2">
              <Button className="bg-red-600 text-white hover:bg-red-500" onClick={handleDelete}>Delete user</Button>
            </div>
          </div>
        </div>
      </main>
      </AdminLayout>
    </>
  )
}

UsersShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
